import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PendingActions,
  Schedule,
  PlayCircle,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import serviceRequestService from '../../services/serviceRequestService';
import moment from 'moment';

const ServiceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [currentTab, setCurrentTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    all: 0,
    pending: 0,
    upcoming: 0,
    ongoing: 0,
    completed: 0,
  });

  useEffect(() => {
    loadServiceRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [currentTab, requests]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadServiceRequests = async () => {
    try {
      setLoading(true);
      const data = await serviceRequestService.getServiceRequests();
      setRequests(data);

      // Calculate stats
      const statsCalc = {
        all: data.length,
        pending: data.filter(r => r.status === 'pending').length,
        upcoming: data.filter(r => r.status === 'upcoming').length,
        ongoing: data.filter(r => r.status === 'ongoing').length,
        completed: data.filter(r => r.status === 'completed').length,
      };
      setStats(statsCalc);
    } catch (error) {
      console.error('Error loading service requests:', error);
      setError('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    if (currentTab === 'all') {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(requests.filter(r => r.status === currentTab));
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: {
        label: 'Pending',
        color: 'warning',
        icon: <PendingActions fontSize="small" />
      },
      upcoming: {
        label: 'Upcoming',
        color: 'info',
        icon: <Schedule fontSize="small" />
      },
      ongoing: {
        label: 'Ongoing',
        color: 'primary',
        icon: <PlayCircle fontSize="small" />
      },
      completed: {
        label: 'Completed',
        color: 'success',
        icon: <CheckCircle fontSize="small" />
      },
      cancelled: {
        label: 'Cancelled',
        color: 'error',
        icon: <Cancel fontSize="small" />
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY hh:mm A');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Service Requests
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track and manage all service requests in your area
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Total</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.all}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #ed6c02' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Pending</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #0288d1' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Upcoming</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.upcoming}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Ongoing</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.ongoing}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Completed</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable">
          <Tab label={`All (${stats.all})`} value="all" />
          <Tab label={`Pending (${stats.pending})`} value="pending" />
          <Tab label={`Upcoming (${stats.upcoming})`} value="upcoming" />
          <Tab label={`Ongoing (${stats.ongoing})`} value="ongoing" />
          <Tab label={`Completed (${stats.completed})`} value="completed" />
        </Tabs>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Booking ID</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Partner</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    No service requests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
                <TableRow key={request.serviceRequestId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {request.bookingId || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.service?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.customerName || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request.mobileNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.partner?.name || 'Not assigned'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.serviceDate ? formatDate(request.serviceDate) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.address || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pincode: {request.partnerPincode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(request.status)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ServiceRequests;
